import { useState, useEffect } from 'react'
import { Download } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { SelectableCard } from '@/components/common/SelectableCard'
import { useQuoteSheetExport } from '@/hooks/useQuoteSheetExport'
import type { PlanDetailItem } from '@/types'

type QuoteSheetType = 'domestic' | 'import'

interface QuoteSheetModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId?: string
  selectedItems: PlanDetailItem[]
  onClearSelection: () => void
}

export function QuoteSheetModal({
  open,
  onOpenChange,
  planId,
  versionId,
  selectedItems,
  onClearSelection,
}: QuoteSheetModalProps) {
  const [selectedType, setSelectedType] = useState<QuoteSheetType | null>(null)

  const {
    isProcessing,
    isCompleted,
    isError,
    progress,
    exportQuoteSheet,
    reset,
  } = useQuoteSheetExport(planId, versionId ?? null)

  // On success: close modal and clear selection
  useEffect(() => {
    if (isCompleted) {
      onOpenChange(false)
      onClearSelection()
    }
  }, [isCompleted, onOpenChange, onClearSelection])

  const handleDownload = () => {
    if (!selectedType) return

    exportQuoteSheet({
      line_item_ids: selectedItems.map(item => item.id),
      type: selectedType,
    })
  }

  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing while processing
    if (isProcessing && !newOpen) return

    if (!newOpen) {
      setSelectedType(null)
      reset()
    }
    onOpenChange(newOpen)
  }

  const statusText = isProcessing
    ? 'Processing...'
    : isError
      ? 'Export failed'
      : ''

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Select quote sheet type
          </DialogTitle>
          <DialogDescription>
            Choose between a domestic or international quote sheet to add
            national or imported products.
          </DialogDescription>
        </DialogHeader>

        {isProcessing || isError ? (
          <div className="flex flex-col gap-3 py-4">
            <Progress value={progress} />
            <p className="text-sm text-muted-foreground text-center">
              {statusText}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 py-2">
              <SelectableCard
                selected={selectedType === 'domestic'}
                onSelect={() => setSelectedType('domestic')}
                title="Domestic"
                variant="compact"
              />
              <SelectableCard
                selected={selectedType === 'import'}
                onSelect={() => setSelectedType('import')}
                title="Imported"
                variant="compact"
              />
            </div>

            <DialogFooter>
              <Button
                onClick={handleDownload}
                disabled={!selectedType}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
